<script lang="ts" setup>
  import type {
    FindOrderOut,
    Maybe,
    OrderInsurance,
  } from '~/types/ecommerce/generated.codegen';

  const props = defineProps<{
    order: FindOrderOut;
    insurances?: OrderInsurance[];
  }>();

  const products = computed(() => props.order?.products);

  function categoryLabel(category?: string | null) {
    if (category === 'Baterias' || category === 'Bateria') {
      return 'Armazenamento e Carregadores';
    }

    return category ?? '';
  }

  const openDatasheet = (datasheet?: Maybe<string>) => {
    if (!datasheet) return;
    window.open(datasheet, '_blank');
  };
</script>

<template>
  <div class="kit-detail-wrapper">
    <div class="kit-detail-main">
      <div class="img-wrapper">
        <SharedImgLazy
          src="/images/empty-image.jpg"
          src-placeholder="/images/empty-image.jpg"
          alt="product-image"
          class="product-image" />
      </div>
      <div class="info-content">
        <template v-for="product in products" :key="product?.sku">
          <div class="product-name">
            <span class="product-name-text">{{ product?.description }}</span>
            <span class="product-sku">{{ product?.sku }}</span>
          </div>
          <div class="products-list">
            <template
              v-for="(detail, index) in product?.details"
              :key="detail?.category ?? index">
              <div class="product-detail">
                <span class="header">{{
                  categoryLabel(detail?.category)
                }}</span>
                <template
                  v-for="(
                    description, descriptionIndex
                  ) in detail?.descriptions"
                  :key="`${description?.name}-${descriptionIndex}`">
                  <span class="description">
                    {{ description?.amount }} {{ description?.name }}
                  </span>
                  <SolButton
                    v-if="description?.datasheet"
                    id="datasheet"
                    size="small"
                    variant="tertiary"
                    @click="openDatasheet(description?.datasheet)">
                    <template #icon-left>
                      <icon-girassol:picture-as-pdf class="icon-size-small" />
                    </template>
                    Datasheet
                  </SolButton>
                </template>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <OrdersOrderInsuranceKitCard
      v-if="insurances"
      class="kit-detail-insurance"
      :insurances="insurances" />
  </div>
</template>

<style scoped lang="scss">
  .kit-detail-wrapper {
    @apply flex flex-col flex-1 min-w-0 w-full overflow-hidden;
    @apply border-1 border-neutral-high-medium rounded-lg p-6;
  }

  .kit-detail-main {
    @apply flex flex-row flex-1 gap-4xs w-full min-w-0;

    .img-wrapper {
      @apply w-15 h-15;

      .product-image {
        @apply w-15 h-15;
      }
    }

    .info-content {
      @apply flex flex-col gap-nano;

      .product-name {
        @apply flex flex-col gap-quark;

        .product-name-text {
          @apply fonts-body-medium-regular text-neutral-low-dark;
        }

        .product-sku {
          @apply fonts-body-small-regular text-neutral-low-light;
        }
      }

      .products-list {
        @apply flex flex-col gap-nano;

        .product-detail {
          @apply flex flex-col;

          .header {
            @apply fonts-body-small-bold text-neutral-low-medium;
          }

          .description {
            @apply fonts-body-small-regular text-neutral-low-light;
          }
        }
      }
    }
  }

  .kit-detail-insurance {
    @apply mt-4 w-full min-w-0;
  }
</style>
